import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';

import { ApiStatus } from 'common/enums';
import { ConfigurationTypes, WhatsNewLinkType } from '../enums/configuration.enum';
import { FieldError, FileDetails } from 'common/models';
import { IErrorResponse } from 'common/models/api-service/error-response.model';
import {
    addCategories,
    addCategoriesByHierarchyId,
    findCategories,
    findCategoriesByHierarchyId,
    setSelectAllCheckbox,
    setCheckbox,
    toggleExpansion
} from 'common/utils/table';

import { arrayMove } from 'common/utils/misc';
import { processNewConfigurationData } from 'common/utils/data';
import {
    ConfigurationWhatsNewVM,
    ConfigurationDetails,
    ConfigurationIndicatorVM,
    ConfigurationListRequest,
    ConfigurationSaveRequest,
    DeleteConfigurationRequest,
    FileUploadLogDataVM,
    HierarchicalExpansionPayload,
    ReorderTrendingDataType,
    TrendingDataSaveRequest,
    ConfigurationWhatsNewDeleteRequest,
    WhatsNewIndicatorSaveRequest,
    ConfigurationReorderUpdateList,
    ConfigurationSaveLinkRequest,
    ConfigurationUploadFileRequest,
    IndicatorSourceDTO,
    VideoLinkDTO,
    VideoDetailsPayload,
    DeleteVideoLinkPayload
} from 'modules/configuration/models/configuration.model';

import initialConfigurationState from './configuration.state';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { ApiInitiator, EntityType } from 'common/enums/data.enum';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';
import { DEFAULT_ERROR_MESSAGE } from 'common/config';

const configurationSlice = createSlice({
    name: 'configurationSlice',
    initialState: initialConfigurationState,
    reducers: {
        fetchTrendingData: (state) => {
            state.trendingData.status = ApiStatus.LOADING;
        },
        fetchTrendingDataSuccess: (
            state,
            { payload }: PayloadAction<ConfigurationIndicatorVM[]>
        ) => {
            state.trendingData.status = ApiStatus.SUCCESS;
            state.trendingData.data = payload;
            state.saveTrendingData = {};
            state.deleteData = {};
            state.reorderData = {};
        },
        fetchTrendingDataFailed: (state, { payload }) => {
            state.trendingData.status = ApiStatus.ERROR;
            state.trendingData.error = payload;
            state.saveTrendingData = {};
            state.deleteData = {};
            state.reorderData = {};
        },
        reorderTrendingData: (state, { payload }: PayloadAction<any>) => {
            state.reorderData.status = ApiStatus.LOADING;
        },
        getCategories: (state, { payload }: PayloadAction<PagedRequest>) => {
            state.hierarchicalData.status = ApiStatus.LOADING;
        },
        getCategoriesSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<MasterDataEntity[] | []>>
        ) => {
            state.hierarchicalData.status = ApiStatus.SUCCESS;
            if (payload.pagination.currentPage === 1) {
                state.hierarchicalData.data = payload;
            } else {
                if (
                    state.hierarchicalData?.data &&
                    state.hierarchicalData.data?.data &&
                    state.hierarchicalData.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...state.hierarchicalData.data.data];
                    updatedData = updatedData.concat([...payload.data]);
                    state.hierarchicalData.data.data = updatedData;
                    state.hierarchicalData.data.pagination = payload.pagination;
                } else {
                    throw new Error(DEFAULT_ERROR_MESSAGE);
                }
            }
        },
        getCategoriesFailed: (state, { payload }: PayloadAction<any>) => {
            state.hierarchicalData.status = ApiStatus.ERROR;
            state.hierarchicalData.error = payload;
        },
        reorderTrendingDataSuccess: (
            state,
            { payload }: PayloadAction<ReorderTrendingDataType>
        ) => {
            const oldIndex = state.trendingData.data?.findIndex(
                (si: ConfigurationIndicatorVM) => si.id === payload.data.id
            );
            state.trendingData.data = arrayMove(
                state.trendingData.data,
                oldIndex || 0,
                payload.newIndex
            );
            state.trendingData.data = state.trendingData.data?.map(
                (si: ConfigurationIndicatorVM, index: number) => {
                    si.order = index + 1;
                    return si;
                }
            );
            state.reorderData.status = ApiStatus.SUCCESS;
        },
        reorderTrendingDataFailed: (state, { payload }) => {
            state.trendingData.status = ApiStatus.ERROR;
            state.trendingData.error = payload;
        },
        fetchKeyIndicators: (state, { payload }: PayloadAction<ConfigurationListRequest>) => {
            state.keyIndicators.status = ApiStatus.LOADING;
        },
        fetchKeyIndicatorsSuccess: (state, { payload }: PayloadAction<ConfigurationDetails>) => {
            state.keyIndicators.status = ApiStatus.SUCCESS;
            state.keyIndicators.data = payload?.configurationDetails;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        fetchKeyIndicatorsFailed: (state, { payload }) => {
            state.keyIndicators.status = ApiStatus.ERROR;
            state.keyIndicators.error = payload;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        reOrderKeyIndicator: (state, { payload }: PayloadAction<any>) => {
            const oldIndex = state.keyIndicators.data?.findIndex(
                (si: ConfigurationIndicatorVM) => si.id === payload.data.id
            );
            state.keyIndicators.data = arrayMove(
                state.keyIndicators.data,
                oldIndex || 0,
                payload.newIndex
            );
            state.keyIndicators.data = state.keyIndicators.data?.map(
                (si: ConfigurationIndicatorVM, index: number) => {
                    si.order = index + 1;
                    return si;
                }
            );
        },

        fetchStaticIndicators: (state, { payload }: PayloadAction<ConfigurationListRequest>) => {
            state.staticIndicators.status = ApiStatus.LOADING;
        },
        fetchStaticIndicatorsSuccess: (state, { payload }: PayloadAction<ConfigurationDetails>) => {
            state.staticIndicators.status = ApiStatus.SUCCESS;
            state.staticIndicators.data = payload?.configurationDetails;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        fetchStaticIndicatorsFailed: (state, { payload }) => {
            state.staticIndicators.status = ApiStatus.ERROR;
            state.staticIndicators.error = payload;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        uploadFile: (state, { payload }: PayloadAction<ConfigurationUploadFileRequest>) => {
            if (!state.uploadStatus) {
                state.uploadStatus = {};
            }
            state.uploadingFiles = payload.files;
            state.selectedFiles = [];
            state.uploadStatus.status = ApiStatus.LOADING;
        },
        uploadFileSuccess: (state) => {
            state.uploadStatus.status = ApiStatus.SUCCESS;
            state.uploadingFiles = [];
            state.isUploadModalVisible = false;
        },
        uploadFileFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            if (state.uploadStatus) {
                state.uploadStatus.status = ApiStatus.ERROR;
                state.uploadStatus.error = payload;
                state.uploadingFiles = [];
            }
        },
        resetUploadStatus: (state) => {
            state.uploadStatus = {};
        },
        setSelectedFiles: (state, { payload }: PayloadAction<Array<FileDetails>>) => {
            state.selectedFiles = payload;
        },
        setUploadingFiles: (state, { payload }: PayloadAction<Array<FileDetails>>) => {
            state.uploadingFiles = payload;
        },
        toggleUploadModal: (state) => {
            state.isUploadModalVisible = !state.isUploadModalVisible;
        },

        setFieldErrors: (state, { payload }: PayloadAction<Array<FieldError>>) => {
            state.fieldErrors = payload;
        },

        deleteData: (state, { payload }: PayloadAction<DeleteConfigurationRequest>) => {
            state.deleteData.status = ApiStatus.LOADING;
        },
        deleteDataSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.deleteData.status = ApiStatus.SUCCESS;
        },
        deleteDataError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteData.status = ApiStatus.ERROR;
            state.deleteData.error = payload;
        },
        resetDeleteData: (state) => {
            state.deleteData = {};
        },
        deleteTrendingData: (state, { payload }: PayloadAction<ConfigurationIndicatorVM>) => {
            state.deleteData.status = ApiStatus.LOADING;
        },
        deleteTrendingDataSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.deleteData.status = ApiStatus.SUCCESS;
        },
        deleteTrendingDataError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteData.status = ApiStatus.ERROR;
            state.deleteData.error = payload;
        },
        deleteWhatsNewData: (
            state,
            { payload }: PayloadAction<ConfigurationWhatsNewDeleteRequest>
        ) => {
            state.deleteData.status = ApiStatus.LOADING;
        },
        deleteWhatsNewSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.deleteData.status = ApiStatus.SUCCESS;
        },
        deleteWhatsNewError: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteData.status = ApiStatus.ERROR;
            state.deleteData.error = payload;
        },
        fetchFrequentlyUsed: (state, { payload }: PayloadAction<ConfigurationListRequest>) => {
            state.frequentlyUsed.status = ApiStatus.LOADING;
        },
        fetchFrequentlyUsedSuccess: (state, { payload }: PayloadAction<ConfigurationDetails>) => {
            state.frequentlyUsed.status = ApiStatus.SUCCESS;
            state.frequentlyUsed.data = payload?.configurationDetails;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        fetchFrequentlyUsedFailed: (state, { payload }) => {
            state.frequentlyUsed.status = ApiStatus.ERROR;
            state.frequentlyUsed.error = payload;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        reOrderData: (state, { payload }: PayloadAction<ConfigurationReorderUpdateList[]>) => {
            state.reorderData.status = ApiStatus.LOADING;
        },
        reOrderDataSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.reorderData.status = ApiStatus.SUCCESS;
        },
        reOrderDataFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.reorderData.status = ApiStatus.ERROR;
            state.reorderData.error = payload;
        },
        resetReOrderData: (state) => {
            state.reorderData = {};
        },
        fetchGeographyOverview: (state, { payload }: PayloadAction<ConfigurationListRequest>) => {
            state.geographyOverview.status = ApiStatus.LOADING;
        },
        fetchGeographyOverviewSuccess: (
            state,
            { payload }: PayloadAction<ConfigurationDetails>
        ) => {
            state.geographyOverview.status = ApiStatus.SUCCESS;
            state.geographyOverview.data = payload?.configurationDetails;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        fetchGeographyOverviewFailed: (state, { payload }) => {
            state.geographyOverview.status = ApiStatus.ERROR;
            state.geographyOverview.error = payload;
            state.deleteData = {};
            state.reorderData = {};
            state.saveIndicator = {};
        },
        fetchWhatsNew: (state) => {
            state.whatsNew.status = ApiStatus.LOADING;
        },
        fetchWhatsNewSuccess: (state, { payload }: PayloadAction<ConfigurationWhatsNewVM[]>) => {
            state.whatsNew.status = ApiStatus.SUCCESS;
            state.whatsNew.data = payload;
            state.deleteData = {};
            state.reorderData = {};
            state.saveWhatsNewIndicator = {};
        },
        fetchWhatsNewFailed: (state, { payload }) => {
            state.whatsNew.status = ApiStatus.ERROR;
            state.whatsNew.error = payload;
            state.deleteData = {};
            state.reorderData = {};
            state.saveWhatsNewIndicator = {};
        },
        reOrderWhatsNew: (state, { payload }: PayloadAction<ConfigurationReorderUpdateList[]>) => {
            state.reorderData.status = ApiStatus.LOADING;
        },
        reOrderWhatsNewSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.reorderData.status = ApiStatus.SUCCESS;
        },
        reOrderWhatsNewFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.reorderData.status = ApiStatus.ERROR;
            state.reorderData.error = payload;
        },
        setCheckboxSelected: (
            state,
            { payload }: PayloadAction<{ isSelected: boolean; data: MasterDataEntity }>
        ) => {
            if (state.hierarchicalData?.data) {
                state.hierarchicalData.data.data = setCheckbox(
                    state.hierarchicalData.data?.data,
                    payload.data.id
                );
            } else {
                throw new Error(DEFAULT_ERROR_MESSAGE);
            }
            if (
                state.selectedIndicatorsForAdd.some(
                    (item: MasterDataEntity) => item.id === payload.data.id
                )
            ) {
                state.selectedIndicatorsForAdd = state.selectedIndicatorsForAdd.filter(
                    (item: MasterDataEntity) => item.id !== payload.data.id
                );
            } else {
                state.selectedIndicatorsForAdd.push(payload.data);
            }
        },
        clearCheckboxSelected: (state) => {
            if (state.hierarchicalData?.data) {
                state.hierarchicalData.data.data = setSelectAllCheckbox(
                    state.hierarchicalData.data.data,
                    false
                );
                state.selectedIndicatorsForAdd = [];
            }
        },
        toggleHierarchicalDataExpansion: (
            state,
            { payload }: PayloadAction<HierarchicalExpansionPayload<EntityVM>>
        ) => {
            const expandedData = toggleExpansion(
                payload.data,
                state.hierarchicalData?.data?.data,
                ApiInitiator.MasterData
            );
            if (state.hierarchicalData.data?.data) {
                state.hierarchicalData.data.data = expandedData;
            } else {
                throw new Error(DEFAULT_ERROR_MESSAGE);
            }
        },

        /**
         *
         * @param state
         * @returns state
         * this toggleHierarchicalDataCollapse dummy action type is created to use while
         * user clicks on collapse button
         * it is used to stop the loader only no other explicit use
         * it will be fired from get getEntitiesByCategory inside configuration.saga.ts
         * file
         */
        toggleHierarchicalDataCollapse: (state) => state,
        toggleHierarchicalExpansionSuccess: (
            state,
            { payload }: PayloadAction<{ parent: EntityVM; subCategories: EntityVM[] }>
        ) => {
            if (payload.subCategories && payload.subCategories.length > 0) {
                payload.subCategories.forEach(
                    (sc: EntityVM) => (sc.parentHierarchyId = payload.parent.hierarchyId)
                );
            }
            let entity: any = {};
            if (payload.parent.entityType === EntityType.Category)
                entity = findCategories(state.hierarchicalData.data?.data, payload.parent.id);
            else
                entity = findCategoriesByHierarchyId(
                    state.hierarchicalData.data?.data,
                    payload.parent.hierarchyId
                );
            if (
                entity.subCategories === undefined ||
                (entity.subCategories && entity.subCategories.length === 0)
            ) {
                state.hierarchicalData.status = ApiStatus.SUCCESS;
                if (
                    state.hierarchicalData.data?.data &&
                    payload.parent.entityType === EntityType.Category
                ) {
                    state.hierarchicalData.data.data = addCategories(
                        state.hierarchicalData.data?.data,
                        payload.parent.id,
                        payload.subCategories
                    );
                } else if (state.hierarchicalData.data?.data) {
                    state.hierarchicalData.data.data = addCategoriesByHierarchyId(
                        state.hierarchicalData.data?.data,
                        payload.parent.hierarchyId,
                        payload.subCategories
                    );
                }
            } else {
                throw new Error(DEFAULT_ERROR_MESSAGE);
            }
        },
        toggleHierarchicalExpansionFailed: (state) => {
            state.hierarchicalData.status = ApiStatus.ERROR;
        },
        setHierarchicalData: (state, { payload }: PayloadAction<any>) => {
            state.hierarchicalData = payload;
        },
        saveIndicatorAdd: (
            state,
            { payload }: PayloadAction<ConfigurationSaveRequest<ConfigurationTypes>>
        ) => {
            state.saveIndicator.status = ApiStatus.LOADING;
        },
        saveIndicatorAddSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.saveIndicator.status = ApiStatus.SUCCESS;
            state.saveIndicator.data = payload;
        },
        saveIndicatorAddFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.saveIndicator.status = ApiStatus.ERROR;
            state.saveIndicator.error = payload;
        },
        resetSaveIndicatorAdd: (state) => {
            state.saveIndicator = {};
        },

        saveTrendingData: (state, { payload }: PayloadAction<TrendingDataSaveRequest>) => {
            state.saveTrendingData.status = ApiStatus.LOADING;
        },
        saveTrendingDataSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.saveTrendingData.status = ApiStatus.SUCCESS;
            state.saveTrendingData.data = payload;
        },
        saveTrendingDataFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.saveTrendingData.status = ApiStatus.ERROR;
            state.saveTrendingData.error = payload;
        },
        resetSaveTrendingData: (state) => {
            state.saveTrendingData = {};
        },

        saveWhatsNewIndicator: (
            state,
            { payload }: PayloadAction<WhatsNewIndicatorSaveRequest<WhatsNewLinkType>>
        ) => {
            state.saveWhatsNewIndicator.status = ApiStatus.LOADING;
        },
        saveWhatsNewIndicatorSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.saveWhatsNewIndicator.status = ApiStatus.SUCCESS;
            state.saveWhatsNewIndicator.data = payload;
        },
        saveWhatsNewIndicatorFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.saveWhatsNewIndicator.status = ApiStatus.ERROR;
            state.saveWhatsNewIndicator.error = payload;
        },
        resetSaveWhatsNewIndicator: (state) => {
            state.saveWhatsNewIndicator = {};
        },
        saveWhatsNewLink: (state, { payload }: PayloadAction<ConfigurationSaveLinkRequest>) => {
            state.saveWhatsNewLink.status = ApiStatus.LOADING;
            state.saveWhatsNewLink.data = payload;
        },
        saveWhatsNewLinkSuccess: (state, { payload }: PayloadAction<boolean>) => {
            if (payload) state.saveWhatsNewLink.status = ApiStatus.SUCCESS;
        },
        saveWhatsNewLinkFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.saveWhatsNewLink.status = ApiStatus.ERROR;
            state.saveWhatsNewLink.error = payload;
        },
        resetWhatsNewLinkStatus: (state) => {
            state.saveWhatsNewLink = {};
        },
        /**
         * Following methods are for getting data of upload file log
         * @param state
         * @param payload
         */
        fetchUploadFileLogBegin: (state, payload) => {
            state.fileUploadLogData.status = ApiStatus.LOADING;
        },
        fetchUploadFileLogSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<FileUploadLogDataVM[]>>
        ) => {
            state.fileUploadLogData.status = ApiStatus.SUCCESS;
            if (payload.pagination.currentPage === 1) {
                state.fileUploadLogData.data = payload;
            } else {
                if (
                    state.fileUploadLogData.data &&
                    state.fileUploadLogData.data.data &&
                    state.fileUploadLogData.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...state.fileUploadLogData.data.data];
                    updatedData = updatedData.concat([...payload.data]);
                    state.fileUploadLogData.data.data = updatedData;
                    state.fileUploadLogData.data.pagination = payload.pagination;
                }
            }
        },
        fetchUploadFileLogError: (state) => {
            state.fileUploadLogData.status = ApiStatus.ERROR;
            state.fileUploadLogData.data = <any>[];
        },
        fetchIndicatorSources: (state, { payload }: PayloadAction<PagedRequest>) => {
            state.indicatorSources.status = ApiStatus.LOADING;
        },
        fetchIndicatorSourcesSuccess: (
            state,
            { payload }: PayloadAction<PaginatedApiResponse<any[]>>
        ) => {
            state.indicatorSources.status = ApiStatus.SUCCESS;
            if (payload.pagination.currentPage === 1) {
                state.indicatorSources.data = payload;
            } else {
                if (
                    state.indicatorSources.data &&
                    state.indicatorSources.data.data &&
                    state.indicatorSources.data.data.length > 0 &&
                    payload.data &&
                    payload.data.length > 0
                ) {
                    let updatedData = [...current(state.indicatorSources.data.data)];
                    updatedData = updatedData.concat([...payload.data]);
                    state.indicatorSources.data.data = updatedData;
                    state.indicatorSources.data.pagination = payload.pagination;
                }
            }
        },
        fetchIndicatorSourcesFailed: (state, { payload }) => {
            state.indicatorSources.status = ApiStatus.ERROR;
            state.indicatorSources.error = payload;
        },
        addIndicatorSource: (state, { payload }: PayloadAction<IndicatorSourceDTO>) => {
            state.addIndicatorSource.status = ApiStatus.LOADING;
        },
        addIndicatorSourceSuccess: (state, { payload }: PayloadAction<IndicatorSourceDTO>) => {
            state.addIndicatorSource.status = ApiStatus.SUCCESS;
            state.addIndicatorSource.data = payload;
        },
        addIndicatorSourceFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.addIndicatorSource.status = ApiStatus.ERROR;
            state.addIndicatorSource.error = payload;
        },
        updateIndicatorSource: (state, { payload }: PayloadAction<IndicatorSourceDTO>) => {
            state.updateIndicatorSource.status = ApiStatus.LOADING;
        },
        updateIndicatorSourceSuccess: (state, { payload }: PayloadAction<IndicatorSourceDTO>) => {
            state.updateIndicatorSource.status = ApiStatus.SUCCESS;
            if (
                state.indicatorSources.data &&
                state.indicatorSources.data?.data &&
                state.indicatorSources.data.data.length > 0
            ) {
                const index = state.indicatorSources.data.data.findIndex(
                    (el: IndicatorSourceDTO) => el.sourceId === payload.sourceId
                );
                state.indicatorSources.data.data[index].sourceName = payload.sourceName;
                state.indicatorSources.data.data[index].webAddress = payload.webAddress;
            }
        },
        updateIndicatorSourceFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.updateIndicatorSource.status = ApiStatus.ERROR;
            state.updateIndicatorSource.error = payload;
        },
        deleteIndicatorSource: (state, { payload }: PayloadAction<IndicatorSourceDTO>) => {
            state.deleteIndicatorSource.status = ApiStatus.LOADING;
        },
        deleteIndicatorSourceSuccess: (state, { payload }: PayloadAction<IndicatorSourceDTO>) => {
            state.deleteIndicatorSource.status = ApiStatus.SUCCESS;
            if (
                state.indicatorSources.data &&
                state.indicatorSources.data?.data &&
                state.indicatorSources.data.data.length > 0
            ) {
                state.indicatorSources.data.data = state.indicatorSources.data.data.filter(
                    (el: IndicatorSourceDTO) => el.sourceId !== payload.sourceId
                );
            }
        },
        deleteIndicatorSourceFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.deleteIndicatorSource.status = ApiStatus.ERROR;
            state.deleteIndicatorSource.error = payload;
        },
        resetIndicatorEditResponse: (state) => {
            state.updateIndicatorSource = {};
        },
        resetIndicatorAddResponse: (state) => {
            state.addIndicatorSource = {};
        },
        saveVideoLink: (
            state,
            {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                payload
            }: PayloadAction<{ payload: VideoDetailsPayload; callBack: (param: boolean) => void }>
        ) => {
            state.saveVideoLink.status = ApiStatus.LOADING;
        },
        updateVideoLink: (
            state,
            {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                payload
            }: PayloadAction<{ payload: VideoDetailsPayload; callBack: (param: boolean) => void }>
        ) => {
            state.updateVideoLink.status = ApiStatus.LOADING;
        },
        saveVideoLinkSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.saveVideoLink.status = ApiStatus.SUCCESS;
            state.saveVideoLink.data = payload;
        },
        saveVideoLinkFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.saveVideoLink.status = ApiStatus.ERROR;
            state.saveVideoLink.error = payload;
        },
        updateVideoLinkSuccess: (state, { payload }: PayloadAction<boolean>) => {
            state.updateVideoLink.status = ApiStatus.SUCCESS;
            state.updateVideoLink.data = payload;
        },
        updateVideoLinkFailed: (state, { payload }: PayloadAction<IErrorResponse>) => {
            state.updateVideoLink.status = ApiStatus.ERROR;
            state.updateVideoLink.error = payload;
        },
        fetchVideoLink: (state) => {
            state.videoLink.status = ApiStatus.LOADING;
        },
        fetchVideoLinkSuccess: (state, { payload }: PayloadAction<any>) => {
            state.videoLink.status = ApiStatus.SUCCESS;
            state.videoLink.data = payload;
        },
        fetchVideoLinkFailed: (state, { payload }) => {
            state.videoLink.status = ApiStatus.ERROR;
            state.videoLink.error = payload;
        },
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        deleteVideoLink: (state, { payload }: PayloadAction<DeleteVideoLinkPayload>) => {
            state.videoLink.status = ApiStatus.LOADING;
        },
        deleteVideoLinkSuccess: (state, { payload }: PayloadAction<DeleteVideoLinkPayload>) => {
            state.videoLink.status = ApiStatus.SUCCESS;
            const clonedState = JSON.parse(JSON.stringify(state.videoLink.data));
            clonedState.splice(
                clonedState.findIndex(
                    (link: VideoLinkDTO) => link.videoDetailId === payload.videoDetailId
                ),
                1
            );
            state.videoLink.data = clonedState;
        },
        deleteVideoLinkFailed: (state, { payload }) => {
            state.videoLink.status = ApiStatus.ERROR;
            state.videoLink.error = payload;
        }
    }
});

export default configurationSlice.reducer;
export const configurationActions = configurationSlice.actions;
